/***
 **Module Name: DisputeDetails
 **File Name :  DisputeDetails.jsx
 **Project :  SPOT SAVER
 **Copyright(c) : SPOT SAVER.
 **Organization : SPOT SAVER
 **author :  Rajesh
 **license :
 **version :  1.0.0
 **Created on : Oct 09 2026
 **Last modified on:
 **Description : The dispute on one visitor payment, opened under its row on
 **              Finance > Transactions > Visitors: status, reason, amount, Stripe's fee,
 **              the evidence deadline, what happened to the venue's share, and a link to
 **              the dispute in Stripe.
 **
 **              Reads what the API (models/MongoDB/bookingDispute.js) keeps on the row:
 **              dispute (the dispute the row follows), disputeShareTakeBack (the venue's
 **              share taken back when it was lost), disputeShareReturned (sent back after
 **              a late win), and otherDisputes (any later dispute on the same charge,
 **              which support handles by hand). Admin-only: the venue portal shows the
 **              badge alone.
 ***/
import React from "react";
import moment from "moment";
import { HEADING, MUTED, BORDER, PANEL, DANGER } from "./stripeStatusMeta";
import { money } from "../revenueFilters";

// Stripe dispute statuses while the bank is still deciding.
export const DISPUTE_OPEN = ["needs_response", "under_review", "warning_needs_response", "warning_under_review"];

const STATUS_LABELS = {
    needs_response: "Needs response",
    under_review: "Under review",
    won: "Won",
    lost: "Lost",
    warning_needs_response: "Inquiry, needs response",
    warning_under_review: "Inquiry under review",
    warning_closed: "Inquiry closed",
    prevented: "Prevented",
};

const REASON_LABELS = {
    bank_cannot_process: "Bank cannot process", credit_not_processed: "Credit not processed",
    customer_initiated: "Customer initiated", debit_not_authorized: "Debit not authorized",
    duplicate: "Duplicate", fraudulent: "Fraudulent", general: "General", incorrect_account_details: "Incorrect account details",
    insufficient_funds: "Insufficient funds", product_not_received: "Product not received",
    product_unacceptable: "Product unacceptable", subscription_canceled: "Subscription canceled", unrecognized: "Unrecognized",
};

// The note under the Paid / Refunded badge once a dispute is over without being lost.
const CLOSED_NOTES = { won: "Dispute won", warning_closed: "Inquiry closed", prevented: "Dispute prevented" };

/** "Dispute won", "Inquiry closed" or "Dispute prevented"; empty otherwise. */
export const disputeNoteOf = (row) => CLOSED_NOTES[row?.disputeStatus] || "";

const otherDisputesOf = (row) => (Array.isArray(row?.otherDisputes) ? row.otherDisputes : []);

/** A visitor payment with a dispute to show under its row. */
export const hasDisputeDetails = (row) => !!row?.dispute || otherDisputesOf(row).length > 0;

const statusLabel = (status) => STATUS_LABELS[status] || String(status || "").replace(/_/g, " ");
const reasonLabel = (reason) => REASON_LABELS[reason] || String(reason || "").replace(/_/g, " ");

const stripeLink = (row, disputeId) =>
    `https://dashboard.stripe.com/${row?.paymentMode === "production" ? "" : "test/"}disputes/${disputeId}`;

// The evidence deadline is red once it is three days away or past.
const dueSoon = (dueBy) => moment(dueBy).diff(moment(), "days", true) <= 3;

/** What happened to the venue's share, as one line, and whether it needs someone. */
const venueShareOf = (row) => {
    const taken = row?.disputeShareTakeBack;
    const returned = row?.disputeShareReturned;
    if (returned?.status === "RETURNED") return { text: `${money(returned.amount)} taken back from the venue when the dispute was lost, then sent back after the late win` };
    if (returned?.status === "FAILED") return { text: `Late win: ${money(returned.amount)} could not be sent back to the venue automatically (${returned.error}). Send it by hand.`, alert: true };
    if (!taken) return null;
    switch (taken.status) {
        case "TAKEN_BACK":
            return { text: `${money(taken.amount)} taken back from the venue` };
        case "NO_VENUE_SHARE":
            return { text: "No share of this payment went to the venue, so nothing was taken back" };
        case "NOTHING_LEFT":
            return { text: "An earlier refund had already taken the venue's share back" };
        default:
            return { text: `The venue's share could not be taken back automatically (${taken.error}). Reverse it by hand in Stripe.`, alert: true };
    }
};

const StripeLink = ({ href }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13 }}>
        Open in Stripe
    </a>
);

const DisputeDetails = ({ data: row, last = true }) => {
    const dispute = row?.dispute;
    const others = otherDisputesOf(row);
    const count = (dispute ? 1 : 0) + others.length;
    const open = DISPUTE_OPEN.includes(dispute?.status);
    const share = venueShareOf(row);

    return (
        <div
            className="px-4 py-3"
            id={`dispute-details-${row?.transactionId}`}
            style={{ background: PANEL, borderTop: `1px solid ${BORDER}`, borderBottom: last ? `1px solid ${BORDER}` : "none" }}
        >
            <div style={{ color: HEADING, fontSize: 14, fontWeight: 700 }} className="mb-1">
                {count > 1 ? `Disputes (${count})` : "Dispute"}
            </div>

            {dispute && (
                <div className="py-2" id={`dispute-entry-${dispute.id}`}>
                    <div className="d-flex flex-wrap align-items-baseline" style={{ columnGap: 16, rowGap: 2, color: MUTED, fontSize: 13 }}>
                        <strong style={{ color: HEADING, fontSize: 14 }}>{money(dispute.amount)}</strong>
                        <span>{statusLabel(dispute.status)}</span>
                        {dispute.reason && <span>{reasonLabel(dispute.reason)}</span>}
                        {dispute.openedAt && <span>Opened {moment(dispute.openedAt).format("MM-DD-YYYY")}</span>}
                        {Number(dispute.fee) > 0 && <span>Stripe dispute fee {money(dispute.fee)}</span>}
                        <StripeLink href={stripeLink(row, dispute.id)} />
                    </div>
                    {open && dispute.evidenceDueBy && (
                        <div className="mt-1" style={{ fontSize: 14, fontWeight: 600, color: dueSoon(dispute.evidenceDueBy) ? DANGER : HEADING }}>
                            Evidence due by {moment(dispute.evidenceDueBy).format("MM-DD-YYYY h:mm A")}
                        </div>
                    )}
                    {share && (
                        <div className="mt-1" style={{ fontSize: 14, color: share.alert ? DANGER : HEADING, fontWeight: share.alert ? 600 : 400 }}>
                            {share.text}
                        </div>
                    )}
                </div>
            )}

            {others.map((d) => (
                <div key={d.id} className="py-2" id={`dispute-entry-${d.id}`} style={{ borderTop: dispute ? `1px solid ${BORDER}` : "none" }}>
                    <div className="d-flex flex-wrap align-items-baseline" style={{ columnGap: 16, rowGap: 2, color: MUTED, fontSize: 13 }}>
                        <strong style={{ color: HEADING, fontSize: 14 }}>{money(d.amount)}</strong>
                        <span>{statusLabel(d.status)}</span>
                        {d.reason && <span>{reasonLabel(d.reason)}</span>}
                        {d.openedAt && <span>Opened {moment(d.openedAt).format("MM-DD-YYYY")}</span>}
                        <StripeLink href={stripeLink(row, d.id)} />
                    </div>
                    {DISPUTE_OPEN.includes(d.status) && d.evidenceDueBy && (
                        <div className="mt-1" style={{ fontSize: 14, fontWeight: 600, color: dueSoon(d.evidenceDueBy) ? DANGER : HEADING }}>
                            Evidence due by {moment(d.evidenceDueBy).format("MM-DD-YYYY h:mm A")}
                        </div>
                    )}
                    <div className="mt-1" style={{ fontSize: 13, color: MUTED }}>
                        Another dispute on this payment. It is not in the reports and does not touch the venue's
                        payouts: handle it by hand in Stripe.
                    </div>
                </div>
            ))}
        </div>
    );
};

export default DisputeDetails;
